import * as React from 'react';
import { MdAdd } from 'react-icons/md';
import { AddButton, Options } from 'ui';
import { SelectMultipleHierarchy } from 'app/area/shared/translated-components';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers/with-open-state';
import { CloseOnBlur } from 'ui/common/close-on-blur';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { AddDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import styles from './document-item-from-multiselect.scss';
import { ComplexValue } from 'app/types-shared';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';

import type { JSX } from 'react';

export interface SafetyLimitMultiSelectProps extends Options<number> {
  textAddItem: string;
  textDone: string;
  textCodeSelectorTitle: string;
  required?: boolean;
  minLevel?: number;
  existingKeys: ComplexValue<number>[];
  onAddDocumentItem?: (selectAddedItem?: boolean, options?: AddDocumentItemOptions) => void;
  onAddDocumentItems?: (selectAddedItem?: boolean, options?: AddDocumentItemOptions[]) => void;
  onRemoveDocumentItems?: (keys: ComplexValue<number>[]) => void;
  onSyncDocumentItems?: (selectedItems: ComplexValue<number>[]) => void;
  placeholderSearch?: string;
  leafNodeOnly?: boolean;
  extraFooterButton?: (onCloseCallback: (onClose: void) => void) => JSX.Element;
  canHideSearchInput?: boolean;
}

interface SafetyLimitMultiSelectState {
  selectedItems: ComplexValue<number>[];
}

class SafetyLimitMultiSelectComponent
  extends React.PureComponent<SafetyLimitMultiSelectProps & OpenStateInjectedProps, SafetyLimitMultiSelectState> {
  private keyMap: KeyMap;
  private handlers: Handlers;

  public constructor(props: SafetyLimitMultiSelectProps & OpenStateInjectedProps) {
    super(props);
    this.state = {
      selectedItems: []
    };

    this.keyMap = {
      handleClose: [keyCodeTypes.Esc],
      handleEnter: [keyCodeTypes.Enter]
    };

    this.handlers = {
      handleClose: this.onClose,
      handleEnter: this.onClose
    };
  }

  private onCodesSelected = (codes: ComplexValue<number>[]) => {
    this.setState({ selectedItems: codes });
  };

  private onConfirm = () => {
    const { existingKeys, onAddDocumentItem, onAddDocumentItems, onRemoveDocumentItems, onSyncDocumentItems } = this.props;
    const { selectedItems } = this.state;

    if (onSyncDocumentItems) {
      onSyncDocumentItems(selectedItems);
      this.clearSelectedItems();
      return;
    }

    const addedItems = selectedItems.filter(s => !existingKeys.some(e => e.value === s.value));
    const removedItems = existingKeys.filter(e => !selectedItems.some(s => s.value === e.value));

    if (removedItems.length) {
      onRemoveDocumentItems && onRemoveDocumentItems(removedItems);
    }

    if (addedItems.length) {
      const codes = addedItems.map(c => ({
        childId: c.value,
        initialValues: { primaryKey: c }
      }));
      if (onAddDocumentItems) {
        onAddDocumentItems(false, codes);
      } else {
        onAddDocumentItem && codes.forEach(c => onAddDocumentItem(false, c));
      }
    }

    this.clearSelectedItems();
  };

  private onClose = () => {
    this.onConfirm();
    this.props.close();
  };

  private openCodeSelector = (_: any) => {
    if (!this.props.isOpen) {
      this.setState({ selectedItems: this.props.existingKeys });
      this.props.open();
    }
  };

  private clearSelectedItems = () => {
    if (this.props.isOpen) {
      this.setState({ selectedItems: [] });
    }
  };

  private renderCodeSelector() {
    const {
      textDone,
      textCodeSelectorTitle,
      minLevel,
      options,
      onLoadOptions,
      fetching,
      loadOptions,
      loadOptionsError,
      placeholderSearch,
      isClosing,
      extraFooterButton,
      canHideSearchInput = true,
      displaySelectAll = false,
      locale
    } = this.props;

    const hideSearchInput = canHideSearchInput && options && !(loadOptions && loadOptions.query);
    const closingAnimation = isClosing ? 'animate-out' : '';

    const valueItems = this.state.selectedItems;

    return (
      <div className={closingAnimation}>
        <DialogExpander
          onClose={this.onClose}
          title={textCodeSelectorTitle}
          textDone={textDone}
          selectExpander={true}
          extraFooterActions={[extraFooterButton?.(this.onClose)]}>
          <SelectMultipleHierarchy
            componentId={'multi-select'}
            className={null}
            onLoadOptions={onLoadOptions}
            options={options}
            valueItems={valueItems}
            onChange={this.onCodesSelected}
            alwaysOpen={true}
            fetching={fetching}
            minLevel={minLevel}
            loadOptions={loadOptions}
            loadOptionsError={loadOptionsError}
            placeholder={placeholderSearch}
            hideSearchInput={hideSearchInput}
            extraFooterButton={extraFooterButton}
            displaySelectAll={displaySelectAll}
            locale={locale}
          />
        </DialogExpander>
      </div>
    );
  }

  public render() {
    const { required, isOpen, textAddItem } = this.props;
    const { documentItemFromMultiSelect, btnWrapper } = styles;

    return (
      <CloseOnBlur onBlur={this.onClose}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
          <div className={documentItemFromMultiSelect}>
            <div className={btnWrapper}>
              <AddButton icon={MdAdd} onClick={this.openCodeSelector} required={required}>
                {textAddItem}
              </AddButton>
            </div>
            {isOpen && this.renderCodeSelector()}
          </div>
        </HotKeys>
      </CloseOnBlur>
    );
  }
}

const Component = withOpenState(true)(SafetyLimitMultiSelectComponent);

export { Component as SafetyLimitMultiSelect };
